import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogComponent, DialogData } from '@shared/dialog/dialog.component';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { isSellingEligibilityApiDetail } from '@core/marketplace-eligibility/api-error-detail-i18n';
import { isNoSubscriptionFoundError } from '@core/subscription/subscription-error.util';
import { isOnboardingContractDialogHandledHttpError } from '@core/subscription/onboarding-contract.service';

/**
 * Determines if an error is a ProtectedError that should be handled by specific components
 * rather than showing a generic "Internal Server Error" dialog
 */

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
  ) {}

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorData = error.error;
        let errorMsg = 'INTL_AN_ERROR_HAS_OCCURED';

        // Handle network connectivity errors (status 0)
        if (error.status === 0) {
          // Don't show error dialogs for network issues - let components handle them
          return throwError(error);
        }

        // Handle CORS errors specifically
        if (this.isCORSError(error)) {
          // For CORS errors, let components handle them individually since they might need different messaging
          return throwError(error);
        }

        if (error.url?.includes('accounts/login-logs')) {
          errorMsg = 'INTL_ERROR_LOADING_LOGIN_LOGS';
        } else if (errorData instanceof ErrorEvent) {
          errorMsg = errorData.message || errorMsg;
        } else {
          // Handle internal
          if (error.status === 500) {
            // Check if this is a ProtectedError that will be handled by specific components
            const isProtectedError = this.isProtectedError(error);

            if (!isProtectedError) {
              this.dialog.open<DialogComponent, DialogData>(DialogComponent, {
                panelClass: 'wai-error-dialog-wrapper',
                data: {
                  title: this.translate.instant('INTL_INTERNAL_SERVER_ERROR_TITLE'),
                  text: this.translate.instant('INTL_INTERNAL_SERVER_ERROR_MSG'),
                  dismissButton: {
                    label: this.translate.instant('INTL_CANCEL'),
                  },
                },
              });
            }

            return throwError(error);
          }

          // Handle common types of back-end errors
          if (Array.isArray(errorData)) {
            errorMsg = errorData.join(', ');
          }
          if (typeof errorData === 'object') {
            const portfolioMessages = this.extractPortfolioValidationMessages(errorData);
            if (portfolioMessages.length > 0) {
              errorMsg = portfolioMessages.join(' • ');
            } else {
              const keys = ['portfolios'];
              errorMsg = Object.entries(errorData)
                .filter(([k]) => !keys.includes(k))
                .map(([, value]) => {
                  if (value == null) return null;
                  if (Array.isArray(value)) {
                    const first = value[0];
                    if (typeof first === 'string') return first;
                    if (typeof first === 'object' && first !== null) return null;
                    return first != null ? String(first) : null;
                  }
                  if (typeof value === 'string') return value;
                  return null;
                })
                .filter((str): str is string => str != null && typeof str === 'string')
                .map((str) => (str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : str))
                .join(', ') || errorMsg;
            }
          }
          if (errorData?.nonFieldErrors) {
            errorMsg = errorData.nonFieldErrors.join(', ');
          }
          // if (errorData?.detail) {
          //   errorMsg = errorData.detail;
          // }
          if (error.status === 401 && errorData?.detail === 'Invalid token.') {
            errorMsg = this.translate.instant('INTL_SESSION_EXPIRED'); // You’ll define this key
          } else if (errorData?.detail) {
            // Ensure detail is a string, not an object
            if (typeof errorData.detail === 'string') {
              errorMsg = errorData.detail;
            } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
              errorMsg = String(errorData.detail[0]);
            } else if (typeof errorData.detail === 'object') {
              // Try to extract a meaningful message from the detail object
              try {
                const detailStr = JSON.stringify(errorData.detail);
                // If it's a simple object or too long, use a generic message instead
                if (detailStr === '{}' || detailStr.length > 200) {
                  errorMsg = 'An error occurred. Please try again.';
                } else {
                  errorMsg = detailStr;
                }
              } catch {
                errorMsg = 'An error occurred. Please try again.';
              }
            } else {
              errorMsg = String(errorData.detail);
            }
          }
        }

        // TODO: temp workaround
        if (this.shouldShowError(error, request)) {
          // this should show proper error, if translation key is not supported in errorMsg
          this.snackBarService.error(errorMsg);
        }
        return throwError(error);
      }),
    ) as Observable<HttpEvent<unknown>>;
  }

  /**
   * Extract validation messages from 400 body when format is
   * { portfolios: [ {}, { stocksPercentage: ["msg"], bondsPercentage: ["msg"], ... }, ... ] }.
   * Handles both camelCase and snake_case keys.
   */
  private extractPortfolioValidationMessages(errorData: { portfolios?: unknown[] }): string[] {
    const messages: string[] = [];
    const portfolios = errorData.portfolios;
    if (!Array.isArray(portfolios)) return messages;

    const keys = [
      'stocksPercentage',
      'stocks_percentage',
      'bondsPercentage',
      'bonds_percentage',
      'privateMarketsPercentage',
      'private_markets_percentage',
    ];
    for (const item of portfolios) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const record = item as Record<string, unknown>;
      for (const key of keys) {
        const val = record[key];
        if (Array.isArray(val)) {
          for (const v of val) {
            if (typeof v === 'string') messages.push(v);
          }
        } else if (typeof val === 'string') {
          messages.push(val);
        }
      }
    }
    return messages;
  }

  private shouldShowError(error: HttpErrorResponse, request?: HttpRequest<unknown>): boolean {
    // Eligibility is communicated via the persistent client banner, not snackbars.
    if (this.isSellingEligibilityError(error)) {
      return false;
    }

    // Suppress noisy error UX while user intentionally logs out.
    if (localStorage.getItem('wai_logout_reason') === 'manual') {
      return false;
    }

    // Don't show errors for theme endpoint - it's handled gracefully with fallback theme
    if (error.url?.includes('/accounts/theme')) {
      return false;
    }

    // Portfolio simulation graphs 400 validation is shown in-page (top banner); skip snackbar to avoid duplicate
    if (error.status === 400 && error.url?.includes('portfolio-simulation/graphs')) {
      return false;
    }

    // Don't show 404 errors for verify-onboarding-fields endpoint - it's handled gracefully by the service
    if (error.status === 404 && error.url?.includes('verify-onboarding-fields')) {
      return false;
    }

    // Missing template files are handled quietly by the template viewer flow.
    if (error.status === 404 && error.url?.includes('/get-template-file')) {
      return false;
    }

    // Reading a data room without access is an expected empty state: the data room views show
    // their own access panel and the Due Diligence tab keeps Launch disabled. Uploads and other
    // data room writes that get a 403 still show the error.
    if (this.isDataRoomListAccessDenied(error, request)) {
      return false;
    }

    // Missing subscription is an expected empty state for some orgs (handled by SubscriptionService).
    if (this.isExpectedMissingSubscriptionError(error)) {
      return false;
    }

    // Onboarding contract + tier-gate errors are handled inside SubscriptionUpgradeDialogComponent.
    if (isOnboardingContractDialogHandledHttpError(error)) {
      return false;
    }

    return !(
      error.url?.match(/\/login|\/password|\/profile/) &&
      (error.error?.authCodeStatus || 'Invalid token' === error.error?.[0])
    );
  }

  private isDataRoomListAccessDenied(error: HttpErrorResponse, request?: HttpRequest<unknown>): boolean {
    if (error.status !== 403 || request?.method !== 'GET') {
      return false;
    }
    const path = (error.url ?? '').split('?')[0];
    return /\/data-room\/nodes\/?$/.test(path);
  }

  private isExpectedMissingSubscriptionError(error: HttpErrorResponse): boolean {
    const path = (error.url ?? '').split('?')[0];
    const isSubscriptionEndpoint =
      path.endsWith('/common/subscription/current/') ||
      path.endsWith('/common/subscription/current') ||
      path.endsWith('/common/subscription/trial-status/') ||
      path.endsWith('/common/subscription/trial-status') ||
      path.endsWith('/common/subscription/change/pending/') ||
      path.endsWith('/common/subscription/change/pending');
    if (!isSubscriptionEndpoint) {
      return false;
    }
    return isNoSubscriptionFoundError(error);
  }

  private isSellingEligibilityError(error: HttpErrorResponse): boolean {
    const detail = error.error?.detail;
    if (typeof detail === 'string') {
      return isSellingEligibilityApiDetail(detail);
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return isSellingEligibilityApiDetail(String(detail[0]));
    }
    return false;
  }

  private isCORSError(error: HttpErrorResponse): boolean {
    // CORS errors typically have:
    // - status === 0 (failed to load)
    // - message containing 'CORS' or 'Access-Control'
    // - name === 'TypeError'
    // - error message containing 'blocked by CORS policy'

    if (error.status === 0 && error.message) {
      const message = error.message.toLowerCase();
      return (
        message.includes('cors') ||
        message.includes('access-control') ||
        message.includes('blocked by cors policy') ||
        message.includes('cross-origin')
      );
    }

    return false;
  }

  /**
   * Determines if an error is a ProtectedError that should be handled by specific components
   * rather than showing a generic "Internal Server Error" dialog
   */
  private isProtectedError(error: HttpErrorResponse): boolean {
    // Extract error message from various possible locations
    let errorMessage = null;

    if (error?.error?.detail) {
      errorMessage = error.error.detail;
    } else if (error?.error && Array.isArray(error.error) && error.error.length > 0) {
      errorMessage = error.error[0];
    } else if (error?.error && typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (error?.statusText) {
      errorMessage = error.statusText;
    }

    if (!errorMessage) {
      return false;
    }

    // Check for ProtectedError patterns
    const protectedErrorPatterns = [
      "Cannot delete some instances of model 'Language'",
      'Cannot delete some instances of model',
      'referenced through protected foreign keys',
      'ProtectedError',
      'violates foreign key constraint',
      'is still referenced from',
      'value too long for type character varying(150)',
    ];

    return protectedErrorPatterns.some((pattern) => errorMessage.includes(pattern));
  }
}
